// GES-434: idempotency keys for POST /api/goods-receipts (API db/771). One key per purchase line per SUBMIT ATTEMPT SET:
//  · a retry / double-click of the same submit sends the same key -> the API returns the original receipt (idempotent_replay)
//    instead of posting a second receipt of the remainder;
//  · a line whose request body changed (the user edited the form) gets a new key, unless that line already COMMITTED in this
//    attempt set (a committed line keeps its key forever: a retry after a partial multi-line failure must replay it, not re-post);
//  · clear() after a fully successful submit: the next submit is a new receipt.
const RECEIPT_CODES = ['RECEIPT_IN_PROGRESS', 'RECEIPT_EXCEEDS_REMAINING', 'IDEMPOTENCY_KEY_REUSED', 'RECEIPT_LINE_CHANGED'];

// crypto.randomUUID only exists in secure contexts (https / localhost); getRandomValues exists everywhere (e.g. http://<lan-ip>).
export function newKey(c = (typeof crypto !== 'undefined' ? crypto : undefined)) {
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function receiptKeySet(gen = newKey) {
  let m = new Map();   // line id -> { key, sig, committed }
  return {
    keyFor(id, body) {
      const sig = JSON.stringify(body), e = m.get(id);
      if (e && (e.committed || e.sig === sig)) return e.key;
      const key = gen(); m.set(id, { key, sig, committed: false }); return key;
    },
    commit(id) { const e = m.get(id); if (e) e.committed = true; },
    clear() { m = new Map(); },
  };
}

// POST one keyed receipt. A 409 RECEIPT_IN_PROGRESS means our own earlier attempt (same key) is still being written: wait and
// ask again, which then returns the replay. `post(body)` = api.requestRaw('/goods-receipts', …) -> { ok, status, data }.
export async function postKeyedReceipt(post, body, { waits = [1500, 3000], sleep = ms => new Promise(r => setTimeout(r, ms)) } = {}) {
  let resp = await post(body);
  for (const ms of waits) {
    if (!(resp.status === 409 && resp.data?.code === 'RECEIPT_IN_PROGRESS')) break;
    await sleep(ms); resp = await post(body);
  }
  return resp;
}

// i18n key (receipt namespace) for the API's concurrency codes; null -> show the server text.
export const receiptCodeKey = code => (RECEIPT_CODES.includes(code) ? 'err.code.' + code : null);
